import { clamp, lerp, clampByte } from './helpers'
import { IRIS_INDICES } from '../avatarInspector/featureIndices'
import { createCanvasAdapter } from './canvasAdapter'

let _canvasAdapter = null
const _scratch = {
  maskCanvas: null,
  maskCtx: null,
  maskW: 0,
  maskH: 0,
  readCanvas: null,
  readCtx: null,
  readW: 0,
  readH: 0
}

export function setCanvasAdapter(adapter) {
  _canvasAdapter = adapter
}

function getAdapter() {
  if (_canvasAdapter) return _canvasAdapter
  try {
    return createCanvasAdapter()
  } catch (e) {
    return {
      createCanvas: (w, h) =>
        typeof OffscreenCanvas !== 'undefined' ? new OffscreenCanvas(w, h) : null,
      getContext: (canvas, type, opts) => (canvas ? canvas.getContext(type, opts) : null)
    }
  }
}

const ensureScratch = (kind, width, height) => {
  const adapter = getAdapter()
  const w = Math.max(1, Math.round(width || 0))
  const h = Math.max(1, Math.round(height || 0))
  if (kind === 'mask') {
    if (!_scratch.maskCanvas) _scratch.maskCanvas = adapter.createCanvas(w, h)
    if (_scratch.maskCanvas) {
      if (_scratch.maskCanvas.width !== w) _scratch.maskCanvas.width = w
      if (_scratch.maskCanvas.height !== h) _scratch.maskCanvas.height = h
      if (!_scratch.maskCtx || _scratch.maskW !== w || _scratch.maskH !== h) {
        _scratch.maskCtx = adapter.getContext(_scratch.maskCanvas, '2d', {
          willReadFrequently: true
        })
      }
      _scratch.maskW = w
      _scratch.maskH = h
      return _scratch.maskCtx
    }
  }
  if (!_scratch.readCanvas) _scratch.readCanvas = adapter.createCanvas(w, h)
  if (_scratch.readCanvas) {
    if (_scratch.readCanvas.width !== w) _scratch.readCanvas.width = w
    if (_scratch.readCanvas.height !== h) _scratch.readCanvas.height = h
    if (!_scratch.readCtx || _scratch.readW !== w || _scratch.readH !== h) {
      _scratch.readCtx = adapter.getContext(_scratch.readCanvas, '2d', {
        willReadFrequently: true
      })
    }
    _scratch.readW = w
    _scratch.readH = h
    return _scratch.readCtx
  }
  return null
}

export const buildFaceMeshAlpha = (width, height, rig) => {
  const alpha = new Uint8ClampedArray(width * height)
  if (!rig?.pointsPx?.length || !rig?.triangles?.length) return alpha
  const maskCtx = ensureScratch('mask', width, height)
  if (!maskCtx) return alpha
  maskCtx.setTransform(1, 0, 0, 1, 0, 0)
  maskCtx.clearRect(0, 0, width, height)
  maskCtx.fillStyle = '#fff'

  for (let i = 0; i < rig.triangles.length; i += 3) {
    const a = rig.pointsPx[rig.triangles[i]]
    const b = rig.pointsPx[rig.triangles[i + 1]]
    const c = rig.pointsPx[rig.triangles[i + 2]]
    if (!a || !b || !c) continue
    maskCtx.beginPath()
    maskCtx.moveTo(a.x, a.y)
    maskCtx.lineTo(b.x, b.y)
    maskCtx.lineTo(c.x, c.y)
    maskCtx.closePath()
    maskCtx.fill()
  }

  const image = maskCtx.getImageData(0, 0, width, height)
  for (let p = 0; p < width * height; p += 1) {
    alpha[p] = image.data[p * 4 + 3]
  }
  return alpha
}

export const buildPolygonAlphaMask = (width, height, pointsPx, polygons) => {
  const alpha = new Uint8ClampedArray(width * height)
  if (!pointsPx?.length || !polygons?.length) return alpha
  const maskCtx = ensureScratch('mask', width, height)
  if (!maskCtx) return alpha
  maskCtx.setTransform(1, 0, 0, 1, 0, 0)
  maskCtx.clearRect(0, 0, width, height)
  maskCtx.fillStyle = '#fff'
  for (let p = 0; p < polygons.length; p += 1) {
    const poly = polygons[p]
    if (!poly?.length) continue
    const first = pointsPx[poly[0]]
    if (!first) continue
    maskCtx.beginPath()
    maskCtx.moveTo(first.x, first.y)
    for (let i = 1; i < poly.length; i += 1) {
      const pt = pointsPx[poly[i]]
      if (!pt) continue
      maskCtx.lineTo(pt.x, pt.y)
    }
    maskCtx.closePath()
    maskCtx.fill()
  }
  const image = maskCtx.getImageData(0, 0, width, height)
  for (let i = 0; i < width * height; i += 1) {
    alpha[i] = image.data[i * 4 + 3]
  }
  return alpha
}

export const dilateMask = (alpha, width, height, radius = 2) => {
  if (!alpha || radius <= 0) return alpha
  const out = new Uint8ClampedArray(alpha)
  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const idx = y * width + x
      if (alpha[idx] > 0) continue
      let found = false
      for (let dy = -radius; dy <= radius && !found; dy += 1) {
        const yy = y + dy
        if (yy < 0 || yy >= height) continue
        for (let dx = -radius; dx <= radius; dx += 1) {
          const xx = x + dx
          if (xx < 0 || xx >= width) continue
          if (alpha[yy * width + xx] > 0) {
            found = true
            out[idx] = 255
            break
          }
        }
      }
    }
  }
  return out
}

export const mergeMasksMax = (primary, secondary) => {
  if (!primary?.length)
    return secondary ? new Uint8ClampedArray(secondary) : new Uint8ClampedArray()
  const out = new Uint8ClampedArray(primary)
  if (!secondary?.length) return out
  const len = Math.min(out.length, secondary.length)
  for (let i = 0; i < len; i += 1) {
    if (secondary[i] > out[i]) out[i] = secondary[i]
  }
  return out
}

export const buildEyeFallbackAlpha = (width, height, rig) => {
  const alpha = new Uint8ClampedArray(width * height)
  if (!rig?.leftEye && !rig?.rightEye) return alpha
  const maskCtx = ensureScratch('mask', width, height)
  if (!maskCtx) return alpha
  maskCtx.setTransform(1, 0, 0, 1, 0, 0)
  maskCtx.clearRect(0, 0, width, height)
  maskCtx.fillStyle = '#fff'

  const drawEyeEllipse = (rect) => {
    if (!rect || rect.w <= 0 || rect.h <= 0) return
    const cx = rect.x + rect.w * 0.5
    const cy = rect.y + rect.h * 0.5
    const rx = Math.max(2, rect.w * 0.54)
    const ry = Math.max(2, rect.h * 0.5)
    maskCtx.beginPath()
    maskCtx.ellipse(cx, cy, rx, ry, 0, 0, Math.PI * 2)
    maskCtx.fill()
  }

  drawEyeEllipse(rig.leftEye)
  drawEyeEllipse(rig.rightEye)

  const image = maskCtx.getImageData(0, 0, width, height)
  for (let i = 0; i < width * height; i += 1) {
    alpha[i] = image.data[i * 4 + 3]
  }
  return dilateMask(alpha, width, height, 1)
}

export const createLayerFromAlpha = (sourceImage, width, height, alpha, opts = {}) => {
  const adapter = getAdapter()
  const layer = adapter.createCanvas(width, height)
  const layerCtx = adapter.getContext(layer, '2d', { willReadFrequently: true })
  layerCtx.drawImage(sourceImage, 0, 0, width, height)
  const pixels = layerCtx.getImageData(0, 0, width, height)
  for (let p = 0; p < width * height; p += 1) {
    const a = alpha[p] || 0
    pixels.data[p * 4 + 3] = (pixels.data[p * 4 + 3] * a) / 255
  }
  // Edge bleed: copy neighbor RGB into near-transparent pixels to avoid dark fringes
  const bleedAlphaMax = Math.max(0, Math.min(255, Number(opts.bleedAlphaMax) || 4))
  const bleedNeighborMin = Math.max(0, Math.min(255, Number(opts.bleedNeighborMin) || 180))
  const bleedIters = Math.max(0, Math.min(6, Number(opts.bleedIters) || 2))
  const doBleed = opts.edgeBleed !== false && bleedIters > 0
  const decontaminateStrength = Math.max(
    0,
    Math.min(1, Number(opts.decontaminateStrength ?? 0.92) || 0)
  )
  const decontaminateIters = Math.max(0, Math.min(4, Number(opts.decontaminateIters) || 2))
  const decontaminateMaxAlpha = Math.max(
    0,
    Math.min(255, Number(opts.decontaminateMaxAlpha) || 252)
  )
  const decontaminateOpaqueMin = Math.max(
    0,
    Math.min(255, Number(opts.decontaminateOpaqueMin) || 208)
  )
  const doDecontaminate = opts.decontaminate !== false && decontaminateStrength > 0
  const bleed = (iters = 2) => {
    const w = width
    const h = height
    for (let it = 0; it < iters; it += 1) {
      const src = new Uint8ClampedArray(pixels.data)
      for (let y = 1; y < h - 1; y += 1) {
        for (let x = 1; x < w - 1; x += 1) {
          const pidx = (y * w + x) * 4
          const a = src[pidx + 3]
          if (a > bleedAlphaMax) continue
          let found = false
          const n = [
            pidx - 4,
            pidx + 4,
            pidx - w * 4,
            pidx + w * 4,
            pidx - w * 4 - 4,
            pidx - w * 4 + 4,
            pidx + w * 4 - 4,
            pidx + w * 4 + 4
          ]
          for (let k = 0; k < n.length; k += 1) {
            const ni = n[k]
            if (src[ni + 3] > bleedNeighborMin) {
              pixels.data[pidx] = src[ni]
              pixels.data[pidx + 1] = src[ni + 1]
              pixels.data[pidx + 2] = src[ni + 2]
              found = true
              break
            }
          }
          if (found) {
            pixels.data[pidx + 3] = a // keep alpha as-is (near zero)
          }
        }
      }
    }
  }
  const decontaminateEdges = (iters = 2) => {
    const w = width
    const h = height
    for (let it = 0; it < iters; it += 1) {
      const src = new Uint8ClampedArray(pixels.data)
      for (let y = 1; y < h - 1; y += 1) {
        for (let x = 1; x < w - 1; x += 1) {
          const pidx = (y * w + x) * 4
          const a = src[pidx + 3]
          if (a <= 0 || a >= decontaminateMaxAlpha) continue
          let sumR = 0
          let sumG = 0
          let sumB = 0
          let sumW = 0
          const n = [
            pidx - 4,
            pidx + 4,
            pidx - w * 4,
            pidx + w * 4,
            pidx - w * 4 - 4,
            pidx - w * 4 + 4,
            pidx + w * 4 - 4,
            pidx + w * 4 + 4
          ]
          for (let k = 0; k < n.length; k += 1) {
            const ni = n[k]
            const na = src[ni + 3]
            if (na < decontaminateOpaqueMin) continue
            const wgt = na / 255
            sumR += src[ni] * wgt
            sumG += src[ni + 1] * wgt
            sumB += src[ni + 2] * wgt
            sumW += wgt
          }
          if (sumW <= 0) continue
          const targetR = sumR / sumW
          const targetG = sumG / sumW
          const targetB = sumB / sumW
          const edgeFactor = 1 - a / 255
          const t = Math.max(0, Math.min(1, decontaminateStrength * (0.35 + edgeFactor * 0.65)))
          pixels.data[pidx] = Math.round(src[pidx] + (targetR - src[pidx]) * t)
          pixels.data[pidx + 1] = Math.round(src[pidx + 1] + (targetG - src[pidx + 1]) * t)
          pixels.data[pidx + 2] = Math.round(src[pidx + 2] + (targetB - src[pidx + 2]) * t)
        }
      }
    }
  }
  if (doBleed) bleed(bleedIters)
  if (doDecontaminate) decontaminateEdges(decontaminateIters)
  layerCtx.putImageData(pixels, 0, 0)
  return layer
}

export const createScleraLayerFromAlpha = (
  sourceImage,
  width,
  height,
  alpha,
  { whitenStrength = 0.94 } = {}
) => {
  const adapter = getAdapter()
  const layer = adapter.createCanvas(width, height)
  const layerCtx = adapter.getContext(layer, '2d', { willReadFrequently: true })
  const pixels = layerCtx.createImageData(width, height)
  let sourceData = null
  if (sourceImage) {
    try {
      layerCtx.drawImage(sourceImage, 0, 0, width, height)
      sourceData = layerCtx.getImageData(0, 0, width, height).data
    } catch {
      sourceData = null
    }
  }
  const strength = clamp(Number(whitenStrength) || 0.94, 0, 1)
  for (let p = 0; p < width * height; p += 1) {
    const srcAlpha = clamp(alpha[p] || 0, 0, 255)
    const di = p * 4
    if (srcAlpha <= 0) {
      pixels.data[di + 3] = 0
      continue
    }
    const outAlpha = Math.min(255, Math.round(srcAlpha * 0.98))
    const sourceLum = sourceData
      ? sourceData[di] * 0.2126 + sourceData[di + 1] * 0.7152 + sourceData[di + 2] * 0.0722
      : 180
    const baseWhite = clamp(234 + sourceLum * 0.07, 228, 255)
    const r = Math.round(lerp(228, baseWhite, strength))
    const g = Math.round(lerp(232, clamp(baseWhite + 2, 0, 255), strength))
    const b = Math.round(lerp(238, clamp(baseWhite + 8, 0, 255), strength))
    pixels.data[di] = r
    pixels.data[di + 1] = g
    pixels.data[di + 2] = b
    pixels.data[di + 3] = outAlpha
  }
  layerCtx.putImageData(pixels, 0, 0)
  return layer
}

export const sampleIrisPalette = (sourceImage, width, height, pointsPx) => {
  if (!sourceImage || !pointsPx?.length || !width || !height) return null
  const readCtx = ensureScratch('read', width, height)
  if (!readCtx) return null
  readCtx.setTransform(1, 0, 0, 1, 0, 0)
  readCtx.clearRect(0, 0, width, height)
  readCtx.drawImage(sourceImage, 0, 0, width, height)
  const image = readCtx.getImageData(0, 0, width, height)
  const data = image.data

  const sampleEye = (centerIdx, ring) => {
    const center = pointsPx[centerIdx]
    if (!center) return { r: 100, g: 142, b: 186 }
    let radius = 3
    if (ring?.length) {
      let sum = 0
      let count = 0
      for (let i = 0; i < ring.length; i += 1) {
        const p = pointsPx[ring[i]]
        if (!p) continue
        sum += Math.hypot(p.x - center.x, p.y - center.y)
        count += 1
      }
      if (count) radius = Math.max(2, sum / count)
    }
    const sampleR = Math.max(2, Math.round(radius * 0.78))
    let sr = 0
    let sg = 0
    let sb = 0
    let total = 0
    for (let dy = -sampleR; dy <= sampleR; dy += 1) {
      const y = Math.round(center.y + dy)
      if (y < 0 || y >= height) continue
      for (let dx = -sampleR; dx <= sampleR; dx += 1) {
        const x = Math.round(center.x + dx)
        if (x < 0 || x >= width) continue
        if (dx * dx + dy * dy > sampleR * sampleR) continue
        const di = (y * width + x) * 4
        const a = data[di + 3]
        if (a < 12) continue
        sr += data[di]
        sg += data[di + 1]
        sb += data[di + 2]
        total += 1
      }
    }
    if (!total) return { r: 100, g: 142, b: 186 }
    return {
      r: clampByte(sr / total),
      g: clampByte(sg / total),
      b: clampByte(sb / total)
    }
  }

  return {
    left: sampleEye(IRIS_INDICES.leftCenter, IRIS_INDICES.leftRing),
    right: sampleEye(IRIS_INDICES.rightCenter, IRIS_INDICES.rightRing)
  }
}

export const countOpaque = (alpha) => {
  let count = 0
  for (let i = 0; i < alpha.length; i += 1) {
    if (alpha[i] > 0) count += 1
  }
  return count
}

export const softenAlphaEdges = (alpha, width, height, { radius = 1 } = {}) => {
  if (!alpha || !width || !height) return alpha
  const r = Math.max(0, Math.min(3, Math.round(radius)))
  if (r <= 0) return alpha
  const out = new Uint8ClampedArray(alpha.length)
  const clampIdx = (v, max) => (v < 0 ? 0 : v > max ? max : v)
  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const idx = y * width + x
      const a = alpha[idx] || 0
      if (a <= 0 || a >= 255) {
        out[idx] = a
        continue
      }
      let sum = 0
      let count = 0
      for (let dy = -r; dy <= r; dy += 1) {
        const yy = clampIdx(y + dy, height - 1)
        const row = yy * width
        for (let dx = -r; dx <= r; dx += 1) {
          const xx = clampIdx(x + dx, width - 1)
          sum += alpha[row + xx] || 0
          count += 1
        }
      }
      out[idx] = Math.round(sum / Math.max(1, count))
    }
  }
  return out
}

export const filterTrianglesByAlpha = (
  triangles,
  pointsPx,
  alpha,
  width,
  height,
  { fallbackToOriginal = true } = {}
) => {
  if (!triangles?.length || !pointsPx?.length || !alpha?.length) return triangles || []
  const filtered = []

  const sampleAlpha = (x, y) => {
    const ix = Math.max(0, Math.min(width - 1, Math.round(x)))
    const iy = Math.max(0, Math.min(height - 1, Math.round(y)))
    return alpha[iy * width + ix] || 0
  }

  for (let i = 0; i < triangles.length; i += 3) {
    const ia = triangles[i]
    const ib = triangles[i + 1]
    const ic = triangles[i + 2]
    const a = pointsPx[ia]
    const b = pointsPx[ib]
    const c = pointsPx[ic]
    if (!a || !b || !c) continue

    const aa = sampleAlpha(a.x, a.y)
    const ba = sampleAlpha(b.x, b.y)
    const ca = sampleAlpha(c.x, c.y)
    const mx = (a.x + b.x + c.x) / 3
    const my = (a.y + b.y + c.y) / 3
    const ma = sampleAlpha(mx, my)

    if (aa > 6 || ba > 6 || ca > 6 || ma > 6) {
      filtered.push(ia, ib, ic)
    }
  }

  if (filtered.length) return filtered
  return fallbackToOriginal ? triangles : []
}

export const buildBorderBackgroundAlpha = (
  sourceImage,
  width,
  height,
  { threshold = 54, sampleStep = 6 } = {}
) => {
  const bgAlpha = new Uint8ClampedArray(width * height)
  if (!sourceImage || !width || !height) return bgAlpha

  const adapter = getAdapter()
  const readCanvas = adapter.createCanvas(width, height)
  const readCtx = adapter.getContext(readCanvas, '2d', { willReadFrequently: true })
  readCtx.drawImage(sourceImage, 0, 0, width, height)
  const image = readCtx.getImageData(0, 0, width, height)
  const data = image.data
  const samples = []

  const pushSample = (x, y) => {
    const idx = (y * width + x) * 4
    const a = data[idx + 3]
    if (a < 8) return
    samples.push([data[idx], data[idx + 1], data[idx + 2]])
  }

  for (let x = 0; x < width; x += sampleStep) {
    pushSample(x, 0)
    pushSample(x, height - 1)
  }
  for (let y = 0; y < height; y += sampleStep) {
    pushSample(0, y)
    pushSample(width - 1, y)
  }
  if (!samples.length) return bgAlpha

  const thresholdSq = threshold * threshold
  const isBgLike = (pixelIndex) => {
    const di = pixelIndex * 4
    const a = data[di + 3]
    if (a < 8) return true
    const r = data[di]
    const g = data[di + 1]
    const b = data[di + 2]
    let minDist = Number.POSITIVE_INFINITY
    for (let i = 0; i < samples.length; i += 1) {
      const sample = samples[i]
      const dr = r - sample[0]
      const dg = g - sample[1]
      const db = b - sample[2]
      const dist = dr * dr + dg * dg + db * db
      if (dist < minDist) minDist = dist
      if (minDist <= thresholdSq) return true
    }
    return false
  }

  const visited = new Uint8Array(width * height)
  const queue = []
  const pushIfValid = (x, y) => {
    if (x < 0 || y < 0 || x >= width || y >= height) return
    const idx = y * width + x
    if (visited[idx]) return
    queue.push(idx)
  }

  for (let x = 0; x < width; x += 1) {
    pushIfValid(x, 0)
    pushIfValid(x, height - 1)
  }
  for (let y = 1; y < height - 1; y += 1) {
    pushIfValid(0, y)
    pushIfValid(width - 1, y)
  }

  while (queue.length) {
    const idx = queue.pop()
    if (visited[idx]) continue
    visited[idx] = 1
    if (!isBgLike(idx)) continue

    bgAlpha[idx] = 255
    const x = idx % width
    const y = Math.floor(idx / width)
    pushIfValid(x - 1, y)
    pushIfValid(x + 1, y)
    pushIfValid(x, y - 1)
    pushIfValid(x, y + 1)
  }

  return dilateMask(bgAlpha, width, height, 1)
}

export default {
  buildFaceMeshAlpha,
  buildPolygonAlphaMask,
  dilateMask,
  mergeMasksMax,
  buildEyeFallbackAlpha,
  createLayerFromAlpha,
  createScleraLayerFromAlpha,
  sampleIrisPalette,
  countOpaque,
  filterTrianglesByAlpha,
  buildBorderBackgroundAlpha
}
